import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCheck, FiLoader, FiUser, FiLock, FiMail, FiPhone, FiUsers, FiShield } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { preCadastrosAPI } from '../services/api';
import Footer from '../components/Footer';
import theme from '../styles/theme';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[6]};
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  max-width: 600px;
  width: 100%;
  padding: ${theme.spacing[8]};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[4]};
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.primary[500]};
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const InfoSection = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const InfoTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[4]} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const InfoItem = styled.li`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const InfoIcon = styled.div`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SubmitButton = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
  color: ${theme.colors.text.secondary};
`;

const PasswordRequirements = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-top: ${theme.spacing[2]};
`;

const RequirementTitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const RequirementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const Requirement = styled.li`
  color: ${props => props.$valid ? theme.colors.success[500] : theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: color 0.2s ease;
`;

const RequirementIcon = styled.span`
  display: inline-block;
`;

const ConfirmarCadastro = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  
  const [preCadastro, setPreCadastro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');

  useEffect(() => {
    carregarPreCadastro();
  }, [uuid]);

  const carregarPreCadastro = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await preCadastrosAPI.buscarPorUuid(uuid);
      
      if (response.data.success) {
        setPreCadastro(response.data.data);
      } else {
        setError('Pré-cadastro não encontrado ou expirado');
      }
    } catch (err) {
      console.error('Erro ao carregar pré-cadastro:', err);
      if (err.response?.status === 404) {
        setError('Este link de confirmação não é válido ou expirou');
      } else {
        setError('Erro ao carregar informações do pré-cadastro');
      }
    } finally {
      setLoading(false);
    }
  };

  // Funções de validação de senha
  const validarSenha = (password) => {
    const temMinimoCaracteres = password.length >= 8;
    const temLetras = /[a-zA-Z]/.test(password);
    const temNumeros = /\d/.test(password);
    const temCaracteresEspeciais = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      temMinimoCaracteres,
      temLetras,
      temNumeros,
      temCaracteresEspeciais,
      valida: temMinimoCaracteres && temLetras && temNumeros && temCaracteresEspeciais
    };
  };

  const validacaoSenha = validarSenha(senha);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!senha || !validacaoSenha.valida) {
      setError('A senha deve atender todos os requisitos');
      return;
    }
    
    if (senha !== confirmarSenha) {
      setError('As senhas não coincidem');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await preCadastrosAPI.confirmar(uuid, senha);
      
      if (response.data.success) {
        setSuccess(true);
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Cadastro confirmado com sucesso! Faça login para continuar.',
              email: preCadastro?.email 
            } 
          });
        }, 2000);
      } else {
        setError(response.data.error || 'Erro ao confirmar cadastro');
      }
    } catch (err) {
      console.error('Erro ao confirmar cadastro:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao confirmar cadastro. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <LoadingContainer>
              <LoadingSpinner>
                <FiLoader />
              </LoadingSpinner>
              <LoadingText>Carregando informações...</LoadingText>
            </LoadingContainer>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  if (error && !preCadastro) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <Header>
              <Icon>
                <FiShield />
              </Icon>
              <Title>Link Inválido</Title>
              <Subtitle>Este link de confirmação não é válido ou expirou</Subtitle>
            </Header>
            <ErrorMessage>{error}</ErrorMessage>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  if (success) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <Header>
              <Icon style={{ background: `${theme.colors.success[500]}20`, color: theme.colors.success[500] }}>
                <FiCheck />
              </Icon>
              <Title>Cadastro Confirmado!</Title>
              <Subtitle>Redirecionando para o login...</Subtitle>
            </Header>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <Card>
          <Header>
            <Icon>
              <FiUser />
            </Icon>
            <Title>Finalize seu Cadastro</Title>
            <Subtitle>Defina uma senha para ativar sua conta</Subtitle>
          </Header>

          <InfoSection>
            <InfoTitle>
              <FiUsers />
              Seus Dados
            </InfoTitle>
            <InfoList>
              <InfoItem>
                <InfoIcon><FiUser /></InfoIcon>
                <strong>{preCadastro?.nome}</strong>
              </InfoItem>
              <InfoItem>
                <InfoIcon><FiMail /></InfoIcon>
                {preCadastro?.email}
              </InfoItem>
              {preCadastro?.telefone && (
                <InfoItem>
                  <InfoIcon><FiPhone /></InfoIcon>
                  {preCadastro.telefone}
                </InfoItem>
              )}
              {preCadastro?.clientes_nomes && (
                <InfoItem>
                  <InfoIcon><FiShield /></InfoIcon>
                  Clientes: {preCadastro.clientes_nomes}
                </InfoItem>
              )}
            </InfoList>
          </InfoSection>

          <Form onSubmit={handleSubmit}>
            {error && !preCadastro && <ErrorMessage>{error}</ErrorMessage>}
            
            <FormGroup>
              <Label htmlFor="senha">
                Senha <Required>*</Required>
              </Label>
              <Input
                id="senha"
                type="password"
                placeholder="Digite sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                disabled={submitting}
              />
              {senha && (
                <PasswordRequirements>
                  <RequirementTitle>Requisitos da senha:</RequirementTitle>
                  <RequirementList>
                    <Requirement $valid={validacaoSenha.temMinimoCaracteres}>
                      <RequirementIcon>{validacaoSenha.temMinimoCaracteres ? '✓' : '○'}</RequirementIcon>
                      Mínimo de 8 caracteres
                    </Requirement>
                    <Requirement $valid={validacaoSenha.temLetras}>
                      <RequirementIcon>{validacaoSenha.temLetras ? '✓' : '○'}</RequirementIcon>
                      Pelo menos uma letra (a-z, A-Z)
                    </Requirement>
                    <Requirement $valid={validacaoSenha.temNumeros}>
                      <RequirementIcon>{validacaoSenha.temNumeros ? '✓' : '○'}</RequirementIcon>
                      Pelo menos um número (0-9)
                    </Requirement>
                    <Requirement $valid={validacaoSenha.temCaracteresEspeciais}>
                      <RequirementIcon>{validacaoSenha.temCaracteresEspeciais ? '✓' : '○'}</RequirementIcon>
                      Pelo menos um caractere especial (!@#$%...)
                    </Requirement>
                  </RequirementList>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmarSenha">
                Confirmar Senha <Required>*</Required>
              </Label>
              <Input
                id="confirmarSenha"
                type="password"
                placeholder="Digite a senha novamente"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                required
                disabled={submitting}
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={submitting || !validacaoSenha.valida || !senha || !confirmarSenha}>
              {submitting ? (
                <>
                  <FiLoader />
                  Confirmando...
                </>
              ) : (
                <>
                  <FiCheck />
                  Confirmar Cadastro
                </>
              )}
            </SubmitButton>
          </Form>
        </Card>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default ConfirmarCadastro;
